import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent, RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import { Search, CircleX } from 'lucide-react';
import { cn, Spinner } from 'shared-ui';
import { useMapSearch } from '@/hooks/useMapSearch';
import type { SearchSuggestion } from '@/api/searchApi';
import type { PoiFeatureProperties } from '@/components/map/utils/poiFeatureProperties';
import { MapControlButton } from './MapControlButton';

interface MapSearchBoxProps {
    mapRef: RefObject<MapRef | null>;
    onPoiSelect?: (poi: PoiFeatureProperties, coords: [number, number]) => void;
    onDmaSelect?: (ident: string) => void;
    /** Fired when the user clears the search — see useMapSearch's clearSearch. */
    onSearchCleared?: () => void;
}

// Width shared by the expanded panel and its suggestions dropdown so they stay aligned.
// Fills the available space up to the desktop max (21.5rem / 344px), but on narrow
// mobile viewports clamps to leave room for the floating sidebar trigger (top-left):
// 100vw − right margin (1rem) − trigger right edge (4.25rem) − gap (0.5rem) = 100vw − 5.75rem.
const PANEL_WIDTH = 'w-[min(21.5rem,calc(100vw-5.75rem))]';

export function MapSearchBox({ mapRef, onPoiSelect, onDmaSelect, onSearchCleared }: MapSearchBoxProps) {
    const { query, setQuery, suggestions, isLoading, handleSelect, clearSearch } = useMapSearch(
        mapRef,
        onPoiSelect,
        onDmaSelect,
        onSearchCleared,
    );

    const [isOpen, setIsOpen] = useState(false);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listboxId = useId();

    const poiSuggestions = suggestions.filter((s) => s.source === 'poi');
    const leakSuggestions = suggestions.filter((s) => s.source === 'leak');
    const dmaSuggestions = suggestions.filter((s) => s.source === 'dma');
    const addressSuggestions = suggestions.filter((s) => s.source === 'address' || !s.source);
    const activeSections = [poiSuggestions, leakSuggestions, dmaSuggestions, addressSuggestions].filter(
        (g) => g.length > 0,
    );
    const isGrouped = activeSections.length > 1;

    const showSuggestions = isOpen && suggestions.length > 0;
    const showEmpty = isOpen && query.length >= 1 && !isLoading && suggestions.length === 0;
    const showLoading = isOpen && query.length >= 1 && isLoading && suggestions.length === 0;
    const showDropdown = showSuggestions || showEmpty || showLoading;

    // Focus input whenever the panel opens
    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    // Close on click / tap outside the container
    useEffect(() => {
        function onPointerDown(e: PointerEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('pointerdown', onPointerDown);
        return () => {
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, []);

    function handleOpenSearch() {
        setIsOpen(true);
    }

    function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
        setQuery(e.target.value);
        setActiveSuggestionIndex(-1);
    }

    // Clear button: with text present it resets the whole search — query, suggestions, the
    // address marker and any search-originated highlight (via onSearchCleared; never a
    // selection). When already empty it just collapses the panel.
    function handleClear() {
        if (query) {
            clearSearch();
        } else {
            setIsOpen(false);
        }
    }

    function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
        if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
            return;
        }

        if (!showSuggestions) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveSuggestionIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveSuggestionIndex((prev) => Math.max(prev - 1, -1));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length) {
                void onSelect(suggestions[activeSuggestionIndex]);
            }
        }
    }

    async function onSelect(suggestion: SearchSuggestion) {
        await handleSelect(suggestion);
        setIsOpen(false);
    }

    const activeDescendantId =
        activeSuggestionIndex >= 0 ? `${listboxId}-option-${String(activeSuggestionIndex)}` : undefined;

    function renderSuggestionItem(suggestion: SearchSuggestion, flatIndex: number) {
        return (
            <li
                key={suggestion.id}
                id={`${listboxId}-option-${String(flatIndex)}`}
                role="option"
                aria-selected={flatIndex === activeSuggestionIndex}
                className={cn(
                    'cursor-pointer px-3 py-2 text-sm transition-colors',
                    flatIndex === activeSuggestionIndex
                        ? 'bg-accent text-accent-foreground'
                        : 'text-foreground hover:bg-accent hover:text-accent-foreground',
                )}
                onPointerDown={(e) => {
                    // Prevent the input losing focus before onClick fires
                    e.preventDefault();
                }}
                onClick={() => void onSelect(suggestion)}
                onMouseEnter={() => {
                    setActiveSuggestionIndex(flatIndex);
                }}
            >
                {suggestion.label}
            </li>
        );
    }

    return (
        // Fixed footprint — always size-10, matching every other map control button.
        // The expanded panel and suggestions dropdown use absolute positioning so they
        // overlay the map and never shift the control stack.
        <div ref={containerRef} className="relative size-10">
            {/* ── Collapsed state ──────────────────────────────────────────────── */}
            {/* Always enabled: POI/DMA search are served by the internal API and need no
                Google key. Google address autocomplete degrades independently — a Google
                init failure must never disable the whole search. */}
            {!isOpen && <MapControlButton icon={Search} label="Search" onClick={handleOpenSearch} />}

            {/* ── Expanded state ────────────────────────────────────────────────
                Anchored right-0 top-0 so the right edge stays at the button
                position while the panel grows leftward. z-20 places it above
                sibling buttons within the MapControls stacking context.       */}
            {isOpen && (
                <div
                    className={cn(
                        'border-border bg-background absolute top-0 right-0 z-20 flex h-10 items-center gap-2 rounded-md border px-3 shadow-md',
                        PANEL_WIDTH,
                    )}
                >
                    {isLoading ? (
                        <Spinner className="text-origin-blue size-4 shrink-0" />
                    ) : (
                        <Search className="text-origin-blue size-4 shrink-0" />
                    )}

                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={handleInputChange}
                        onKeyDown={handleKeyDown}
                        placeholder="Search Network"
                        className="text-foreground placeholder:text-input placeholder:font-poppins min-w-0 flex-1 bg-transparent text-sm italic outline-none placeholder:text-sm placeholder:leading-5 placeholder:font-normal placeholder:italic"
                        aria-label="Search Network"
                        role="combobox"
                        aria-expanded={showSuggestions}
                        aria-controls={listboxId}
                        aria-autocomplete="list"
                        aria-activedescendant={activeDescendantId}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                    />

                    <button
                        type="button"
                        aria-label={query ? 'Clear search' : 'Close search'}
                        onClick={handleClear}
                        className="text-muted-foreground hover:text-foreground flex shrink-0 cursor-pointer items-center transition-colors"
                    >
                        <CircleX className="size-[13.33px]" strokeWidth={2} />
                    </button>
                </div>
            )}

            {/* ── Suggestions dropdown ──────────────────────────────────────────
                Also anchored right-0 and matches the expanded panel width.
                top-full positions it just below the 40px container.           */}
            {showDropdown && (
                <ul
                    id={listboxId}
                    role="listbox"
                    aria-label="Search suggestions"
                    className={cn(
                        'border-border bg-background absolute top-full right-0 z-20 mt-1 max-h-[min(20rem,calc(100dvh-8rem))] overflow-y-auto rounded-md border shadow-md',
                        PANEL_WIDTH,
                    )}
                >
                    {showLoading && (
                        <li className="text-muted-foreground flex items-center gap-2 px-3 py-2 text-sm">
                            <Spinner className="size-3.5" />
                            Searching…
                        </li>
                    )}
                    {showEmpty && <li className="text-muted-foreground px-3 py-2 text-sm">No results found.</li>}
                    {showSuggestions &&
                        !isGrouped &&
                        suggestions.map((suggestion, index) => renderSuggestionItem(suggestion, index))}
                    {showSuggestions && isGrouped && (
                        <>
                            {poiSuggestions.length > 0 && (
                                <>
                                    <li
                                        role="presentation"
                                        className="border-border text-muted-foreground border-b px-3 py-1 text-xs font-medium"
                                    >
                                        Points of Interest
                                    </li>
                                    {poiSuggestions.map((suggestion) =>
                                        renderSuggestionItem(suggestion, suggestions.indexOf(suggestion)),
                                    )}
                                </>
                            )}
                            {leakSuggestions.length > 0 && (
                                <>
                                    <li
                                        role="presentation"
                                        className="border-border text-muted-foreground border-t border-b px-3 py-1 text-xs font-medium"
                                    >
                                        Leaks
                                    </li>
                                    {leakSuggestions.map((suggestion) =>
                                        renderSuggestionItem(suggestion, suggestions.indexOf(suggestion)),
                                    )}
                                </>
                            )}
                            {dmaSuggestions.length > 0 && (
                                <>
                                    <li
                                        role="presentation"
                                        className="border-border text-muted-foreground border-t border-b px-3 py-1 text-xs font-medium"
                                    >
                                        Districts
                                    </li>
                                    {dmaSuggestions.map((suggestion) =>
                                        renderSuggestionItem(suggestion, suggestions.indexOf(suggestion)),
                                    )}
                                </>
                            )}
                            {addressSuggestions.length > 0 && (
                                <>
                                    <li
                                        role="presentation"
                                        className="border-border text-muted-foreground border-t border-b px-3 py-1 text-xs font-medium"
                                    >
                                        Addresses
                                    </li>
                                    {addressSuggestions.map((suggestion) =>
                                        renderSuggestionItem(suggestion, suggestions.indexOf(suggestion)),
                                    )}
                                </>
                            )}
                        </>
                    )}
                </ul>
            )}
        </div>
    );
}
